/**
 * MoneyPositionWidget — cash and bank, month by month.
 *
 * COST: ZERO extra Firestore reads. Every figure comes from the scoreboard
 * document the KPI cards already load (see src/utils/scoreboardData.js), so
 * this panel is free to add.
 */

import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts';
import { FiTrendingUp } from 'react-icons/fi';
import { useReducedMotion } from 'framer-motion';
import { useAuth } from '../../context/AuthContext';
import useCurrency from '../../hooks/useCurrency';
import { colors } from '../../theme/colors';
import { getScoreboard, scoreboardUsable, scoreboardMonthly } from '../../utils/scoreboardData';

const CASH = '#1a73e8';
const BANK = colors.primary.main; // maroon

const ChartTip = ({ active, payload, label, formatCurrency }) => {
  if (!active || !payload || !payload.length) return null;
  const p = payload[0].payload || {};
  return (
    <TipBox>
      <TipLabel>{label}</TipLabel>
      <TipRow><TipDot $c={CASH} /><TipName>Cash</TipName><TipValue>{formatCurrency(p.cash || 0)}</TipValue></TipRow>
      <TipRow><TipDot $c={BANK} /><TipName>Bank</TipName><TipValue>{formatCurrency(p.bank || 0)}</TipValue></TipRow>
    </TipBox>
  );
};

const MoneyPositionWidget = () => {
  const prefersReduced = useReducedMotion();
  const { activeCompany, activeFinancialYear, activeBranch } = useAuth();
  const { formatCurrency } = useCurrency();
  const companyId = activeCompany?.id;
  const fyId = activeFinancialYear?.id || null;
  const branchScoped = !!(activeBranch && activeBranch.id !== 'all');

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    if (!companyId || !fyId) { setLoading(false); setRows([]); return undefined; }
    setLoading(true);
    (async () => {
      try {
        const board = await getScoreboard(companyId);
        if (cancelled) return;
        setRows(scoreboardUsable(board, fyId) ? scoreboardMonthly(board, fyId, 8) : []);
      } catch (e) {
        if (!cancelled) setRows([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [companyId, fyId]);

  if (!companyId) return null;

  const hasData = rows.length > 1;
  const latest = rows[rows.length - 1];

  return (
    <Card>
      <Header>
        <HeaderLeft>
          <IconWrap><FiTrendingUp size={15} /></IconWrap>
          <div>
            <Title>Money Position</Title>
            <Sub>Cash and bank, month by month</Sub>
          </div>
        </HeaderLeft>
        {hasData && (
          <Totals>
            <T><Swatch $c={CASH} />{formatCurrency(latest.cash)}</T>
            <T><Swatch $c={BANK} />{formatCurrency(latest.bank)}</T>
          </Totals>
        )}
      </Header>

      {loading ? (
        <Skeleton />
      ) : !hasData ? (
        <EmptyBox>Not enough history yet — this builds up month by month.</EmptyBox>
      ) : (
        <>
          <ResponsiveContainer width="100%" height={200}>
            <AreaChart data={rows} margin={{ top: 8, right: 8, left: -12, bottom: 0 }}>
              <defs>
                <linearGradient id="mpCash" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={CASH} stopOpacity={0.3} />
                  <stop offset="100%" stopColor={CASH} stopOpacity={0} />
                </linearGradient>
                <linearGradient id="mpBank" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={BANK} stopOpacity={0.22} />
                  <stop offset="100%" stopColor={BANK} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border-light)" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 11, fill: 'var(--color-text-tertiary)' }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: 'var(--color-text-tertiary)' }} axisLine={false} tickLine={false}
                     tickFormatter={(v) => Math.abs(v) >= 1000000 ? `${(v / 1000000).toFixed(1)}M` : Math.abs(v) >= 1000 ? `${Math.round(v / 1000)}K` : v} />
              <Tooltip content={<ChartTip formatCurrency={formatCurrency} />} />
              {/* Draw once, left to right. Recharts animates by default, so the
                  job here is to CAP it (600ms) and switch it off entirely for
                  users who asked for reduced motion. */}
              <Area type="monotone" dataKey="cash" stroke={CASH} strokeWidth={2.5} fill="url(#mpCash)"
                    isAnimationActive={!prefersReduced} animationDuration={600} animationBegin={120} />
              <Area type="monotone" dataKey="bank" stroke={BANK} strokeWidth={2.5} fill="url(#mpBank)"
                    isAnimationActive={!prefersReduced} animationDuration={600} animationBegin={200} />
            </AreaChart>
          </ResponsiveContainer>
          {branchScoped && (
            <Footnote>Showing company-wide figures — this trend is not split by branch.</Footnote>
          )}
        </>
      )}
    </Card>
  );
};

export default MoneyPositionWidget;

/* ───────────── styles ───────────── */
const Card = styled.div`
  background: var(--color-background-default);
  border: 1px solid var(--color-border-light);
  border-radius: 14px;
  padding: 16px;
  height: 100%;
`;
const Header = styled.div`display:flex;align-items:flex-start;justify-content:space-between;margin-bottom:12px;gap:12px;flex-wrap:wrap;`;
const HeaderLeft = styled.div`display:flex;align-items:center;gap:10px;`;
const IconWrap = styled.div`
  width:32px;height:32px;border-radius:9px;display:flex;align-items:center;justify-content:center;
  background:${colors.primary.main}15;color:${colors.primary.main};
`;
const Title = styled.div`font-size:13.5px;font-weight:700;color:var(--color-text-primary);`;
const Sub = styled.div`font-size:11px;color:var(--color-text-tertiary);margin-top:2px;`;
const Totals = styled.div`display:flex;gap:14px;flex-wrap:wrap;`;
const T = styled.span`font-size:12px;font-weight:700;color:var(--color-text-primary);display:inline-flex;align-items:center;`;
const Swatch = styled.i`width:9px;height:9px;border-radius:3px;background:${p => p.$c};display:inline-block;margin-right:6px;`;
const EmptyBox = styled.div`
  padding:36px 16px;text-align:center;font-size:12.5px;color:var(--color-text-tertiary);
`;
const Skeleton = styled.div`
  height:200px;border-radius:10px;
  background:linear-gradient(90deg,var(--color-border-light) 25%,transparent 37%,var(--color-border-light) 63%);
  background-size:400% 100%;animation:mpshimmer 1.4s ease infinite;
  @keyframes mpshimmer{0%{background-position:100% 50%}100%{background-position:0 50%}}
`;
const Footnote = styled.div`font-size:10.5px;color:var(--color-text-tertiary);margin-top:8px;`;
const TipBox = styled.div`
  background:var(--color-background-default);border:1px solid var(--color-border-light);
  border-radius:9px;padding:9px 11px;box-shadow:0 4px 14px rgba(0,0,0,.09);
`;
const TipLabel = styled.div`font-size:11px;font-weight:700;margin-bottom:6px;color:var(--color-text-primary);`;
const TipRow = styled.div`display:flex;align-items:center;gap:7px;font-size:11.5px;margin-top:3px;`;
const TipDot = styled.span`width:8px;height:8px;border-radius:2px;background:${p => p.$c};`;
const TipName = styled.span`color:var(--color-text-secondary);`;
const TipValue = styled.span`margin-left:auto;font-weight:700;color:var(--color-text-primary);`;
